import {
  ParsedXerSchedule,
  ScheduleImportSession,
  ScheduleTaskMapping,
} from '../../../../utils/xer'

const SESSION_STORAGE_KEY = 'didge_schedule_import_session'

type ScheduleImportStore = {
  session: ScheduleImportSession | null
}

let memoryStore: ScheduleImportStore = { session: null }

function loadFromSessionStorage(): ScheduleImportSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) {
      return null
    }
    return JSON.parse(raw) as ScheduleImportSession
  } catch {
    return null
  }
}

function persist(session: ScheduleImportSession | null) {
  memoryStore = { session }
  try {
    if (session) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    }
  } catch {
    // sessionStorage may be unavailable or quota exceeded; keep in-memory only
  }
}

export function getScheduleImportSession(): ScheduleImportSession | null {
  if (memoryStore.session) {
    return memoryStore.session
  }
  const fromStorage = loadFromSessionStorage()
  if (fromStorage) {
    memoryStore = { session: fromStorage }
  }
  return fromStorage
}

export function startScheduleImportSession(
  fileName: string,
  schedule: ParsedXerSchedule,
): ScheduleImportSession {
  const session: ScheduleImportSession = {
    fileName,
    projectPublicId: null,
    schedule,
    mappings: [],
  }
  persist(session)
  return session
}

export function setScheduleImportProject(
  projectPublicId: string | null,
): ScheduleImportSession | null {
  const current = getScheduleImportSession()
  if (!current) {
    return null
  }
  const next: ScheduleImportSession = {
    ...current,
    projectPublicId,
    // Clear mappings when project changes — trackers are project-scoped
    mappings:
      current.projectPublicId === projectPublicId ? current.mappings : [],
  }
  persist(next)
  return next
}

export function setScheduleImportMappings(
  mappings: ScheduleTaskMapping[],
): ScheduleImportSession | null {
  const current = getScheduleImportSession()
  if (!current) {
    return null
  }
  const next: ScheduleImportSession = {
    ...current,
    mappings,
  }
  persist(next)
  return next
}

export function clearScheduleImportSession() {
  persist(null)
}
